import { useCallback, useEffect, useState } from "react";
import type { ProductionSegmentType, TimeRangeFinite } from "types/types";
import {
  askrClients,
  PROCESS_QUERY,
  PRODUCTION_SEGMENTS_QUERY,
} from "providers/AskrProvider";
import { QueryResult, useQuery } from "@apollo/client";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useDispatch } from "react-redux";
import { addNewSegments } from "providers/GlobalStateProvider";
import { HUBLET_TAGS_QUERY, midgardClients } from "providers/MidgardProvider";
import { atom, useAtom } from "jotai";
import {
  useVisibleProcesses,
  useWorkScheduleIdOnCurrentPage,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";

/** Fetch a process from Askr by its machemetryId */
export function useProcessQuery({
  machemetryId,
  skip,
}: {
  machemetryId: string;
  skip: boolean;
}): [{ id: string }, QueryResult<{ id: string }, { machemetryId: string }>] {
  const ckey = useCustomerKey();
  const askrClient = askrClients.get(ckey);
  const results = useQuery(PROCESS_QUERY, {
    client: askrClient,
    variables: { machemetryId },
    skip: skip || !machemetryId,
  });
  return [results?.data?.process, results];
}

/** Fetch the hublet tags of a process from Midgard by its machemetryId */
export function useHubletTagsQuery({
  machemetryId,
  skip = false,
}: {
  machemetryId: string;
  skip?: boolean;
}): [any, QueryResult<any, { machemetryId: string }>] {
  const ckey = useCustomerKey();
  const midgardClient = midgardClients.get(ckey);
  const [hubletTagsMap, setHubletTagsMap] = useAtom(hubletTagsAtom);

  // Use tags from global state if available to avoid redundant fetching
  const results = useQuery(HUBLET_TAGS_QUERY, {
    client: midgardClient,
    variables: { machemetryId },
    skip: skip || !machemetryId || !!hubletTagsMap[machemetryId],
  });

  useEffect(() => {
    if (results?.data?.hubletTags && machemetryId) {
      setHubletTagsMap(prev => ({
        ...prev,
        [machemetryId]: results.data.hubletTags,
      }));
    }
  }, [results?.data?.hubletTags, machemetryId, setHubletTagsMap]);

  // Return tags from global state if available, otherwise from query results
  return [hubletTagsMap[machemetryId] || results?.data?.hubletTags, results];
}

function useProductionSegmentsQuery({
  machemetryId,
  ge,
  lt,
  skip,
}: {
  machemetryId: string;
  ge: number;
  lt: number;
  skip: boolean;
}): [
  ProductionSegmentType[] | undefined,
  QueryResult<any, { machemetrySourceName: string; ge: string; lt: string }>,
] {
  const missingVariable = !machemetryId || !ge || !lt;
  const ckey = useCustomerKey();
  const askrClient = askrClients.get(ckey);
  const results = useQuery(PRODUCTION_SEGMENTS_QUERY, {
    client: askrClient,
    variables: {
      machemetrySourceName: machemetryId,
      ge: String(ge),
      lt: String(lt),
    },
    skip: skip || missingVariable,
  });
  return [results?.data?.productionSegments, results];
}

/** if we just retrotagged -> refetch affected data in AggregatesAndSegmentsGetters (Askr) or DataDumpGetter with useCache: false (Midgard) */
const justRetrotaggedAtom = atom<boolean>(false);

/** Global storage for hublet tags to avoid multiple queries */
export const hubletTagsAtom = atom<Record<string, any>>({});
/** if we just retrotagged, we'll need to refetch the affected data */
export function useJustRetrotagged() {
  return useAtom(justRetrotaggedAtom);
}

export function useForceRerenderWhenRetrotagChanges() {
  const [justRetrotagged] = useJustRetrotagged();
  const [rerenderKey, setRerenderKey] = useState(0);
  useEffect(() => {
    if (justRetrotagged) {
      setRerenderKey(p => p + 1);
    }
  }, [justRetrotagged]);
  return rerenderKey;
}

export function useForceRerenderWhenWorkScheduleChanges() {
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const [rerenderKey, setRerenderKey] = useState(0);
  useEffect(() => {
    if (workScheduleId) {
      setRerenderKey(p => p + 1);
    }
  }, [workScheduleId]);
  return rerenderKey;
}

export function useRefetchMutatedSegments({
  machemetryId,
  retrotaggedSegmentRange,
}: {
  machemetryId: string;
  retrotaggedSegmentRange: TimeRangeFinite | null;
}) {
  const dispatch = useDispatch();
  // only refetch segments after we click "confirm" button
  const [, setJustRetrotagged] = useJustRetrotagged();
  const [, { refetch }] = useProductionSegmentsQuery({
    machemetryId,
    // need to fetch the first segments right before and after the retrotagged segment
    ge: Number(retrotaggedSegmentRange?.ge) - 1,
    lt: Number(retrotaggedSegmentRange?.lt) + 1,
    skip:
      !machemetryId ||
      !retrotaggedSegmentRange?.ge ||
      !retrotaggedSegmentRange?.lt,
  });

  return useCallback(
    async (...args: any[]) => {
      setJustRetrotagged(true);
      /* this function only cares about providing the refetch function,
    so segments should only be overwritten on a refetch */
      return refetch(...args).then(({ data }) => {
        try {
          const newSegments = data.productionSegments;
          dispatch(addNewSegments(machemetryId, newSegments));
          setJustRetrotagged(false);
        } catch (error) {
          console.log(error);
        }
      });
    },
    [dispatch, machemetryId, refetch, setJustRetrotagged]
  );
}

/**
 * Prefetch hublet tags for a process so that the "open tag sheet" button
 * doesn't start in a loading state when the RetrotagPopper is opened
 * Only runs when on a hublet page to avoid unnecessary API calls
 */
export function usePrefetchHubletTags() {
  const ckey = useCustomerKey();
  const midgardClient = midgardClients.get(ckey);
  const hubletPageProcessId = useProcessIdOnSingleProcessPage();
  const { isHubletPage, isTimelinePage, isGridLayoutPage, isTrendsPage } =
    useGetPageFromPath();
  const isTimelineVisible =
    isTimelinePage || isHubletPage || isGridLayoutPage || isTrendsPage;
  const visibleProcesses = useVisibleProcesses();
  const [, setHubletTagsMap] = useAtom(hubletTagsAtom);

  useEffect(() => {
    if (hubletPageProcessId && (isHubletPage || isGridLayoutPage)) {
      // on hublet page, fetch single-process hublet tags
      midgardClient
        .query({
          query: HUBLET_TAGS_QUERY,
          variables: { machemetryId: hubletPageProcessId },
          // This ensures the data will be updated if it changes
          fetchPolicy: "network-only",
        })
        .then(response => {
          setHubletTagsMap(prev => ({
            ...prev,
            [hubletPageProcessId]: response.data.hubletTags,
          }));
        })
        .catch(error => {
          // Silently catch errors - we don't want to break the UI
          // if prefetching fails
          console.error("Failed to prefetch hublet tags:", error);
        });
    } else if (isTimelineVisible && visibleProcesses.length > 0) {
      // on other pages with timelines, fetch hublet tags for all visible processes
      visibleProcesses.forEach(process => {
        if (process?.id) {
          midgardClient
            .query({
              query: HUBLET_TAGS_QUERY,
              variables: { machemetryId: process.id },
              // This ensures the data will be updated if it changes
              fetchPolicy: "network-only",
            })
            .then(response => {
              setHubletTagsMap(prev => ({
                ...prev,
                [process.id]: response.data.hubletTags,
              }));
            })
            .catch(error => {
              // Silently catch errors - we don't want to break the UI
              // if prefetching fails
              console.error("Failed to prefetch hublet tags:", error);
            });
        }
      });
    }
  }, [
    hubletPageProcessId,
    midgardClient,
    isHubletPage,
    isTimelineVisible,
    visibleProcesses,
    isGridLayoutPage,
    setHubletTagsMap,
  ]);
}
